/* The catalog: search, source tabs, sort, and three ways to look at the result — cards,
   a ranked list, and the audits table. Filtering and sorting happen on the server; the
   page asks for 60 at a time. */
import React, { useEffect, useRef, useState } from "react";
import { FileCode2, LayoutGrid, List, Search, ShieldCheck, Sparkles, Star } from "lucide-react";

import { isSkillActionLive, useSkills } from "@/shared/api/skills";
import { AuditPill, AuditShield, SourceBadge } from "@/shared/components/skill-atoms";
import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { PageHeader } from "@/shared/components/ui-kit";
import { useSettled } from "@/shared/hooks/useSettled";

import { AUDITORS, SKILL_SOURCES, SORTS, formatCount } from "../catalog";
import { Byline, LoadingDots, OwnerAvatar } from "./atoms";
import NewSkillMenu, { type NewSkillWay } from "./NewSkillMenu";

import type { LucideIcon } from "lucide-react";
import type { Skill, SkillSort, SkillSource } from "@/shared/types";

type ViewMode = "grid" | "list" | "audits";

const NOT_AUDITED = "Made in this workspace: skills.sh's partners audit the public catalog only";

const VIEW_MODES: { key: ViewMode; Icon: LucideIcon; title: string }[] = [
  { key: "grid", Icon: LayoutGrid, title: "Card grid" },
  { key: "list", Icon: List, title: "Ranked list" },
  { key: "audits", Icon: ShieldCheck, title: "Security audits" },
];

/** The repo a card links to: a publisher's page. A workspace's own skill says who made it instead. */
function RepoLink({ skill, onOpenRepo, className }: { skill: Skill; onOpenRepo: (repo: string) => void; className: string }) {
  if (skill.source === "workspace") return <Byline skill={skill} className={className} />;
  if (!skill.repo) return <span className={className}>{skill.owner.login}</span>;
  const repo = skill.repo;
  return (
    <span
      role="link"
      tabIndex={-1}
      onClick={(e) => { e.stopPropagation(); onOpenRepo(repo); }}
      className={`${className} hover:underline hover:text-[var(--text-primary)]`}
    >
      {repo}
    </span>
  );
}

export function SkillCard({ skill, onOpen, onOpenRepo }: {
  skill: Skill;
  onOpen: (id: string) => void;
  onOpenRepo: (repo: string) => void;
}) {
  return (
    <button
      onClick={() => onOpen(skill.id)}
      className="card-hover press text-left rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] p-4 flex flex-col gap-3 min-w-0"
    >
      <div className="flex items-start gap-3">
        <OwnerAvatar owner={skill.owner} />
        <div className="min-w-0 flex-1">
          {/* The name wraps (at its hyphens, usually) rather than truncating */}
          <div className="min-w-0 break-words leading-[1.35]">
            <span className="text-[14px] font-medium text-[var(--text-primary)] tracking-[-0.005em]">{skill.name}</span>
            <span className="inline-flex items-center gap-1 ml-1 align-[-2px]">
              <SourceBadge source={skill.source} />
            </span>
          </div>
          <RepoLink skill={skill} onOpenRepo={onOpenRepo} className="inline-block text-[11.5px] text-[var(--text-tertiary)] mt-[1px] truncate" />
        </div>
      </div>
      <p className="text-[12.5px] leading-[1.55] text-[var(--text-secondary)] line-clamp-2 m-0">{skill.description}</p>
      {/* Each fact stays on one line; on a narrow card they wrap as a whole */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 whitespace-nowrap text-[11.5px] text-[var(--text-tertiary)] mt-auto tabular-nums">
        {skill.stars != null && (
          <span className="inline-flex items-center gap-1" title="GitHub stars of the repo">
            <Star className="w-3 h-3" strokeWidth={1.75} />
            {formatCount(skill.stars)}
          </span>
        )}
        <AuditShield audits={skill.audits} withLabel note={skill.source === "workspace" ? NOT_AUDITED : undefined} />
        <span className="inline-flex items-center gap-1">
          <FileCode2 className="w-3 h-3" strokeWidth={1.75} />
          {skill.file_count} file{skill.file_count === 1 ? "" : "s"}
        </span>
        {skill.license && <span className="ml-auto font-mono">{skill.license}</span>}
      </div>
    </button>
  );
}

function SkillListRow({ skill, rank, onOpen, onOpenRepo }: {
  skill: Skill;
  rank: number;
  onOpen: (id: string) => void;
  onOpenRepo: (repo: string) => void;
}) {
  return (
    <button
      onClick={() => onOpen(skill.id)}
      className="press w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--accent-hover)] border-t hairline first:border-t-0"
    >
      <span className="w-5 shrink-0 text-right text-[11.5px] tabular-nums text-[var(--text-quaternary)]">{rank}</span>
      <OwnerAvatar owner={skill.owner} size={28} rounded={8} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="-mr-0.5 text-[13.5px] font-medium text-[var(--text-primary)] tracking-[-0.005em] truncate">{skill.name}</span>
          <SourceBadge source={skill.source} size={12} />
          <RepoLink skill={skill} onOpenRepo={onOpenRepo} className="text-[11.5px] text-[var(--text-tertiary)] truncate" />
        </div>
        <div className="text-[12px] text-[var(--text-secondary)] truncate mt-[1px]">{skill.description}</div>
      </div>
      {skill.stars != null && (
        <span className="shrink-0 hidden sm:inline-flex items-center gap-1 text-[12px] tabular-nums text-[var(--text-tertiary)]">
          <Star className="w-3 h-3" strokeWidth={1.75} />
          {formatCount(skill.stars)}
        </span>
      )}
      <AuditShield audits={skill.audits} />
    </button>
  );
}

function AuditsTable({ skills, onOpen }: { skills: Skill[]; onOpen: (id: string) => void }) {
  return (
    <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-2 bg-[var(--surface-sunk)] text-[12.5px] text-[var(--text-tertiary)]">
        <span className="flex-1">Skill</span>
        {AUDITORS.map((a) => (
          <span key={a.key} className="w-[104px] text-left hidden lg:block whitespace-nowrap">{a.label.split(" ")[0]}</span>
        ))}
      </div>
      {skills.map((s) => (
        <button
          key={s.id}
          onClick={() => onOpen(s.id)}
          className="press w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[var(--accent-hover)] border-t hairline"
        >
          <div className="flex-1 min-w-0 flex items-center gap-2.5">
            <OwnerAvatar owner={s.owner} size={24} rounded={7} />
            <span className="text-[13px] font-medium truncate">{s.name}</span>
            <span className="text-[11.5px] text-[var(--text-tertiary)] truncate hidden md:block">{s.repo}</span>
          </div>
          {s.source === "workspace" ? (
            // no partner audits a workspace's own skill: one cell across their columns, not five "Not yet"s
            <span className="relative hidden lg:flex gap-3">
              {AUDITORS.map((a) => <span key={a.key} className="w-[104px]" />)}
              <span className="absolute inset-0 flex items-center text-[12px] text-[var(--text-tertiary)]">Not audited: made in this workspace</span>
            </span>
          ) : AUDITORS.map((a) => (
            <span key={a.key} className="w-[104px] hidden lg:block">
              <AuditPill status={s.audits[a.key] ?? "none"} />
            </span>
          ))}
          <span className="lg:hidden">
            <AuditShield audits={s.audits} withLabel note={s.source === "workspace" ? NOT_AUDITED : undefined} />
          </span>
        </button>
      ))}
    </div>
  );
}

export default function SkillsCatalog({ onOpen, onOpenRepo, onNew, canAsk }: {
  onOpen: (id: string) => void;
  onOpenRepo: (repo: string) => void;
  onNew: (way: NewSkillWay) => void;
  canAsk: boolean;
}) {
  const [query, setQuery] = useState("");
  const [source, setSource] = useState<"all" | SkillSource>("all");
  const [sort, setSort] = useState<SkillSort>("stars");
  const [mode, setMode] = useState<ViewMode>("grid");
  const searchRef = useRef<HTMLInputElement>(null);
  const q = useSettled(query.trim());

  const list = useSkills({ q: q || undefined, source: source === "all" ? undefined : source, sort });
  const skills = list.data?.pages.flatMap((p) => p.items) ?? [];
  const total = list.data?.pages[0]?.total ?? 0;
  const filtered = !!q || source !== "all";

  // "/" focuses search from anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && !/INPUT|TEXTAREA/.test(document.activeElement?.tagName || "")) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const empty = list.isSuccess && total === 0 && !filtered;

  return (
    <>
      <PageHeader
        title={<Breadcrumb>Skills</Breadcrumb>}
      >
        <div className="flex items-center gap-2 px-2.5 h-8 w-full min-w-[110px] max-w-[240px] rounded-[8px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] focus-within:ring-[var(--hairline-focus)]">
          <Search className="w-3.5 h-3.5 shrink-0 text-[var(--text-tertiary)]" strokeWidth={1.75} />
          <input
            ref={searchRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search skills"
            aria-label="Search skills"
            className="flex-1 min-w-0 bg-transparent text-[13px] outline-none placeholder:text-[var(--text-quaternary)]"
          />
          <kbd className="hidden sm:inline text-[10.5px] px-1 py-[1px] rounded-[4px] ring-1 ring-[var(--hairline)] text-[var(--text-quaternary)]">/</kbd>
        </div>
        <NewSkillMenu onPick={onNew} canAsk={canAsk} />
      </PageHeader>

      {empty ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 fade-in">
          <div className="w-10 h-10 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
            <Sparkles className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
          </div>
          <div className="text-[15px] font-medium tracking-[-0.005em]">No skills yet</div>
          <div className="text-[13px] text-[var(--text-tertiary)] mt-1 max-w-[320px] text-center">
            Skills your agents can load will be listed here.
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-[1160px] mx-auto px-4 md:px-6 py-6 fade-in-up">
            {/* Source tabs + count + sort + view mode */}
            <div className="flex items-center gap-2 pb-4 flex-wrap">
              {/* on a phone the tabs scroll in place, as SectionNav's do, rather than the page */}
              <div className="flex items-center max-w-full overflow-x-auto no-scrollbar rounded-[9px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] p-[3px]">
                {SKILL_SOURCES.filter((t) => t.key !== "workspace" || isSkillActionLive("workspaceTab")).map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setSource(t.key)}
                    className={`press shrink-0 whitespace-nowrap px-3 py-[4px] rounded-[7px] text-[12.5px] font-medium ${
                      source === t.key
                        ? "bg-[var(--surface)] text-[var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.03)] ring-1 ring-[var(--hairline)]"
                        : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
              {list.isSuccess && (
                <span className="text-[11.5px] text-[var(--text-tertiary)] tabular-nums pl-1">
                  {total.toLocaleString()} skill{total === 1 ? "" : "s"}
                </span>
              )}
              <div className="flex-1" />
              {mode !== "audits" && (
                <div className="flex items-center gap-0.5">
                  {SORTS.map((s) => (
                    <button
                      key={s.key}
                      onClick={() => setSort(s.key)}
                      className={`press h-7 px-2.5 rounded-full text-[12px] whitespace-nowrap ${
                        sort === s.key
                          ? "bg-[var(--accent-active)] text-[var(--text-primary)] font-medium"
                          : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--accent-hover)]"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-center rounded-[9px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] p-[3px]">
                {VIEW_MODES.map(({ key, Icon, title }) => (
                  <button
                    key={key}
                    title={title}
                    aria-label={title}
                    onClick={() => setMode(key)}
                    className={`press w-7 h-6 flex items-center justify-center rounded-[6px] ${
                      mode === key
                        ? "bg-[var(--surface)] text-[var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.03)] ring-1 ring-[var(--hairline)]"
                        : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                ))}
              </div>
            </div>

            {list.isPending && <LoadingDots label="Loading skills" />}

            {list.isError && !skills.length && (
              <div className="py-16 flex flex-col items-center gap-3 text-center">
                <div className="text-[13px] text-[var(--text-secondary)]">Couldn't load skills.</div>
                <button
                  onClick={() => list.refetch()}
                  className="press h-8 px-3.5 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[13px] text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] hover:ring-[var(--hairline-focus)]"
                >
                  Try again
                </button>
              </div>
            )}

            {mode === "grid" && skills.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {skills.map((s) => <SkillCard key={s.id} skill={s} onOpen={onOpen} onOpenRepo={onOpenRepo} />)}
              </div>
            )}

            {mode === "list" && skills.length > 0 && (
              <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
                {skills.map((s, i) => (
                  <SkillListRow key={s.id} skill={s} rank={i + 1} onOpen={onOpen} onOpenRepo={onOpenRepo} />
                ))}
              </div>
            )}

            {mode === "audits" && skills.length > 0 && (
              <>
                <AuditsTable skills={skills} onOpen={onOpen} />
                <p className="text-[12px] text-[var(--text-tertiary)] leading-[1.6] mt-3 mb-0">
                  Audits come from skills.sh's security partners and are refreshed on every import. “Not yet” means that
                  partner hasn't scanned the skill.
                </p>
              </>
            )}

            {list.isSuccess && total === 0 && filtered && (q || source !== "workspace") && (
              <div className="py-16 text-center text-[13px] text-[var(--text-tertiary)]">
                {q ? `No skills match “${q}”.` : "No skills from this source yet."}
              </div>
            )}

            {list.isSuccess && total === 0 && !q && source === "workspace" && (
              <div className="py-16 flex flex-col items-center gap-3 fade-in">
                <div className="w-10 h-10 rounded-[12px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] flex items-center justify-center">
                  <Sparkles className="w-[18px] h-[18px] text-[var(--text-tertiary)]" strokeWidth={1.5} />
                </div>
                <div className="text-[14px] font-medium">No workspace skills yet</div>
                <div className="text-[12px] text-[var(--text-tertiary)] mt-1 max-w-[340px] text-center leading-[1.55]">
                  A skill your team makes — uploaded, written here, drafted by Definable or copied from GitHub — is listed here, ready to put on any agent.
                </div>
                <NewSkillMenu onPick={onNew} canAsk={canAsk} align="center" />
              </div>
            )}

            {list.hasNextPage && (
              <div className="flex justify-center pt-4">
                <button
                  onClick={() => list.fetchNextPage()}
                  disabled={list.isFetchingNextPage}
                  className="press h-8 px-3.5 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--surface)] text-[13px] text-[var(--text-primary)] hover:bg-[var(--surface-sunk)] hover:ring-[var(--hairline-focus)] disabled:opacity-40 disabled:pointer-events-none"
                >
                  {list.isFetchingNextPage ? "Loading…" : `Show more · ${(total - skills.length).toLocaleString()} left`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
